import { useEffect, useState } from 'react';

import { Box, Button, Card, Paragraph, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserManagementApplicationPermissionWebApiHandler_createPermissionRequest,
  useRentalManagerUserManagementApplicationPermissionWebApiHandler_getApplicationManagerForCompany,
  useRentalManagerUserPropertySummaryWebApiHandler_applications,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import applicationUtils from '../../../../utils/applicationUtils';
import stringHelper from '../../../../utils/stringHelper';

import { PERMISSION_CARD_TEXT, PERMISSION_REQUEST_TYPE } from '../../../../constants/ApplicationPermissionsConstants';
import { CLIPBOARD_ILLUSTRATION_URL } from '../../../../constants/DigitalLibraryConstants';

import EmptyState from '../../../../components/EmptyState';
import SpotIllustration from '../../../../components/EmptyState/SpotIllustration';

import { useAuth } from '../../../AuthProvider';

import type {
  GetUserPropertyDetailsApplicationsSummaryWebDto,
  UserPropertySummaryApplicationsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  permissionType: keyof typeof PERMISSION_REQUEST_TYPE;
  onSetApplicationManagerClick: () => void;
  applicationsSummary?: UserPropertySummaryApplicationsResponseWebDto | GetUserPropertyDetailsApplicationsSummaryWebDto;
  listingAlias: string;
};

const ApplicationPermissionCard = ({
  applicationsSummary,
  permissionType,
  onSetApplicationManagerClick,
  listingAlias,
}: Props) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const { data: applicationManager } =
    useRentalManagerUserManagementApplicationPermissionWebApiHandler_getApplicationManagerForCompany();

  const { refetch: createPermissionRequest } =
    useRentalManagerUserManagementApplicationPermissionWebApiHandler_createPermissionRequest(
      { userManagementCompanyId: user?.userManagementCompanyId ?? '' },
      { enabled: false },
    );

  const [permissionRequestSent, setPermissionRequestSent] = useState<boolean | undefined>();

  useEffect(() => {
    if (applicationsSummary?.userManagementFlags) {
      setPermissionRequestSent(applicationsSummary.userManagementFlags.isPermissionRequestSent);
    }
  }, [applicationsSummary]);

  if (!applicationsSummary) {
    return null;
  }

  const {
    isApplicationsAccepted,
    rentalApplicationData: { totalNumberOfApplications },
  } = applicationsSummary;

  const handleRequestAccessClick = async () => {
    try {
      await createPermissionRequest();
      const appManagerName = applicationUtils.getName(applicationManager || {});
      const successBody = `Application permission request sent to ${stringHelper.wrapText(appManagerName, 10)}`;
      enqueueToast(
        <Toast tone="success" className="fs-mask">
          {successBody}
        </Toast>,
      );
      queryClient.invalidateQueries({
        queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias }),
      });
      setPermissionRequestSent(true);
    } catch {
      enqueueToast(<Toast tone="critical">{'Permission request couldn’t be sent. Try again.'}</Toast>, {
        duration: 10000,
      });
    }
  };

  const handleButtonClick =
    permissionType === PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER
      ? onSetApplicationManagerClick
      : handleRequestAccessClick;

  const renderActions = () => {
    if (permissionType === PERMISSION_REQUEST_TYPE.NO_PERMISSION) {
      return null;
    }

    if (permissionRequestSent) {
      return (
        <Box css={{ display: 'flex', marginTop: 'tight' }}>
          <Text textStyle="body-sm">Waiting for permission</Text>
          <TextButton onClick={handleButtonClick} css={{ paddingLeft: 'tight' }}>
            Re-send request
          </TextButton>
        </Box>
      );
    }

    return (
      <Button css={{ marginTop: 'loose' }} tone="brand" emphasis="filled" fluid onClick={handleButtonClick}>
        {PERMISSION_CARD_TEXT[permissionType]?.buttonText}
      </Button>
    );
  };

  const renderBody = () => (
    <>
      <Paragraph textStyle="body-sm-bold" css={{ marginBottom: 'tight' }}>
        {PERMISSION_CARD_TEXT[permissionType]?.subHeading}
      </Paragraph>
      <Paragraph textStyle="body-sm">{PERMISSION_CARD_TEXT[permissionType]?.body}</Paragraph>
    </>
  );

  const renderHeadingText = () => {
    if (totalNumberOfApplications > 0) {
      return `${stringHelper.pluralize(totalNumberOfApplications, 'application', 'applications')} awaiting review`;
    }

    if (isApplicationsAccepted) {
      return 'This property is accepting applications';
    }

    return 'This property does not currently accept applications';
  };

  return (
    <Card
      data-testid="ApplicationCompanyNoPermissionCard"
      tone="neutral"
      css={{
        paddingInline: 'looser',
        paddingBlock: 'looser',
        height: '100%',
      }}
      elevated={false}
    >
      <EmptyState
        useCard={false}
        align="left"
        headingLevel={3}
        icon={<SpotIllustration src={CLIPBOARD_ILLUSTRATION_URL} />}
        heading={renderHeadingText()}
        body={renderBody()}
        actions={renderActions()}
      />
    </Card>
  );
};

export default ApplicationPermissionCard;
